import { FC, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { cloneDeep } from "lodash";
import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { BaselineDialogProps, FormType } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
// eslint-disable-next-line import/no-named-as-default
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";
import {
  ShrimpFacilityOutputNames,
  ShrimpFacilityOutputs,
} from "../../../../models/Facility/Processing";
import { FacilityTableItem } from "../../../../models/Facility/FacilityTypes";
import OutputDefinitionsDialog from "../Slaughterhouse_v2/OutputDefinitionsDialog";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import ShrimpOutputFormContent from "./ShrimpOutputFormContent";
import {
  ShrimpBaseline,
  ShrimpProcessingOutput,
  ShrimpProcessingStageData,
} from "../../../../models/Baseline/ShrimpBaseline";

interface ShrimpOutputFormDialogProps
  extends BaselineDialogProps {
  formTitle: string;
  facility: FacilityTableItem | undefined;
  iconCode?: string;
}

const ShrimpOutputFormDialog: FC<
  ShrimpOutputFormDialogProps
> = ({
  handleCancel,
  facility,
  formType = FormType.Add,
  itemIndex = 0,
  formVisible = false,
  formTitle = "Output",
  iconCode = "arrows/arrow-circle-broken-right",
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;

  const fieldItemPrefix = `stages.${itemIndex}.stageData.processingOutput`;

  const currResetValue = useRef<ShrimpProcessingOutput>();
  const formContext = useFormContext<ShrimpBaseline>();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const [openPdfDialog, setOpenPdfDialog] = useState<boolean>(false);

  useEffect(() => {
    if (formVisible) {
      currResetValue.current = cloneDeep<ShrimpProcessingOutput>(
        formContext.getValues(fieldItemPrefix)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible]);

  const handleCloseDialog = () => {
    if (currResetValue.current) {
      const resetObject = { ...formContext.getValues() };
      const stageData = resetObject.stages[itemIndex]
        ?.stageData as ShrimpProcessingStageData;
      if (stageData?.processingOutput) {
        stageData.processingOutput = currResetValue.current;
        formContext.reset(resetObject, { errors: true });
      }
    }

    if (handleCancel) handleCancel();
  };

  const handleOpenDefinitionsDialog = () => {
    setOpenPdfDialog(true);
  };

  const handleCloseDefinitionsDialog = () => {
    setOpenPdfDialog(false);
  };

  return (
    <div>
      {openPdfDialog ? (
        <OutputDefinitionsDialog
          open
          handleClose={handleCloseDefinitionsDialog}
          isShrimp
        />
      ) : (
        <DialogContainer
          formVisible={formVisible}
          handleClose={handleCloseDialog}
          iconCode={iconCode}
          formTitle={formTitle} 
          introText={intl.formatMessage({
            id: "SUSTELL.STAGES.PROCESSING.OUTPUT.SHRIMP.DESCRIPTION",
          }, { br: <br /> })}
          width="1280px"
        >
          <DsmButton variant="text" onClick={handleOpenDefinitionsDialog}>
            <DsmIcon slot="before" name="general/eye" />
            {intl.formatMessage({
              id: "DATABASE_FOUNDATION_DEFINITIONS",
            })}
          </DsmButton>

          <DsmGrid className={classes.dsmGridOneColumn}>
            {Object.entries(ShrimpFacilityOutputs).map(([key, val]) => (
              <ShrimpOutputFormContent
                key={`output-content-${key}`}
                fieldItemPrefix={fieldItemPrefix}
                fieldName={key as ShrimpFacilityOutputNames}
                formType={formType}
                labelName={val as ShrimpFacilityOutputs}
                facility={facility}
              />
            ))}
          </DsmGrid>
          <DsmButtonControlGroup
            cancelHandler={handleCloseDialog}
            saveHandler={async () => {
              await formContext.trigger(
                `stages.${itemIndex}.stageData.processingOutput`
              );
              handleCancel("confirm");
            }}
            saveLabel={intl.formatMessage({ id: "GENERAL.CONFIRM" })}
          />
        </DialogContainer>
      )}
    </div>
  );
};

export default ShrimpOutputFormDialog;
